import { useCallback, useEffect, useState } from "react";
import { Sparkles, Mail } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { streamAI } from "@/lib/aiStream";
import { logger } from "@/lib/logger";

const RANGES = [7, 30, 90];

const pct = (now, before) => {
  if (!before) return now ? "new" : "—";
  return `${now >= before ? "+" : ""}${Math.round(((now - before) / before) * 100)}%`;
};

/** Plain-English weekly briefing written by Claude from live platform metrics. */
export default function AdminInsights() {
  const [days, setDays] = useState(7);
  const [data, setData] = useState(null);
  const [summary, setSummary] = useState("");
  const [busy, setBusy] = useState(false);
  const [emailing, setEmailing] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/admin/insights", { params: { days } });
      setData(r.data.stats);
      setSummary(r.data.summary || "");
    } catch (err) {
      logger.error("[admin] insights load failed", err);
      toast.error("Could not load insights");
    }
  }, [days]);

  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    setBusy(true);
    setSummary("");
    try {
      await streamAI(`/admin/insights?days=${days}`, {}, (full) => setSummary(full));
      toast.success("Briefing ready");
    } catch (e) {
      toast.error(e.message || "Could not write the briefing");
    } finally {
      setBusy(false);
    }
  };

  const emailNow = async () => {
    setEmailing(true);
    try {
      const r = await api.post("/admin/insights/email");
      if (r.data.ok) {
        toast.success(`Briefing emailed to ${r.data.recipients.join(", ")}`);
        load();
      } else {
        toast.error(r.data.error || r.data.skipped || "Could not send the email");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send the email");
    } finally {
      setEmailing(false);
    }
  };

  const cur = data?.current;
  const prev = data?.previous;

  return (
    <div className="space-y-8" data-testid="admin-insights">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="flex gap-0 border-2 border-ink">
          {RANGES.map((d) => (
            <button key={d} onClick={() => setDays(d)}
              className={`px-5 py-3 font-mono uppercase text-xs tracking-widest font-bold border-l-2 first:border-l-0 border-ink ${days === d ? "bg-ink text-acid" : "bg-white"}`}
              data-testid={`insights-range-${d}`}>Last {d}d</button>
          ))}
        </div>
        <button onClick={generate} disabled={busy} className="brut-btn-acid disabled:opacity-40" data-testid="insights-generate">
          <Sparkles className={`w-4 h-4 mr-2 ${busy ? "animate-pulse" : ""}`} />
          {busy ? "Writing..." : summary ? "Refresh briefing" : "Write my briefing"}
        </button>
        <button onClick={emailNow} disabled={emailing} className="brut-btn disabled:opacity-40" data-testid="insights-email">
          <Mail className={`w-4 h-4 mr-2 ${emailing ? "animate-pulse" : ""}`} />
          {emailing ? "Sending..." : "Email it to me now"}
        </button>
      </div>

      {cur && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-0 border-2 border-ink">
          <Cell label="Revenue" v={`$${cur.revenue.total.toFixed(2)}`} sub={`${pct(cur.revenue.total, prev?.revenue.total)} vs prev`} hot />
          <Cell label="Jobs posted" v={cur.jobs_posted} sub={`${pct(cur.jobs_posted, prev?.jobs_posted)} vs prev`} />
          <Cell label="Leads unlocked" v={cur.lead_unlocks} sub={`$${cur.unlock_revenue.toFixed(2)} of credit spent`} />
          <Cell label="Unsold leads" v={cur.jobs_never_unlocked} sub={cur.avg_lead_score ? `avg quality ${cur.avg_lead_score}/100` : "no scores yet"} last />
        </div>
      )}

      <div className="brut-card bg-white p-6" data-testid="insights-summary">
        <div className="overline mb-3">// Owner briefing {data?.summary_generated_at ? "" : ""}</div>
        {summary ? (
          <pre className="whitespace-pre-wrap font-sans text-sm leading-relaxed">{summary}</pre>
        ) : (
          <p className="text-sm text-muted-foreground">
            No briefing yet for this window. Hit “Write my briefing” and Claude will read the numbers
            below and tell you, in plain English, what is working and what to do next.
          </p>
        )}
        <p className="font-mono text-[10px] mt-6 text-muted-foreground">
          This briefing is also emailed to every admin automatically every Monday morning.
        </p>
      </div>

      {cur && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <TradeList title="Top trades by jobs posted" rows={cur.top_trades_by_jobs} suffix=" jobs" testid="insights-trades-jobs" />
          <TradeList title="Top trades by lead revenue" rows={cur.top_trades_by_revenue} money testid="insights-trades-revenue" />
        </div>
      )}

      {data && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-0 border-2 border-ink">
          <Cell label="Pending approvals" v={data.platform.pending_contractor_approvals} />
          <Cell label="Active subscriptions" v={data.platform.active_subscriptions} />
          <Cell label="Open disputes" v={data.platform.open_disputes} />
          <Cell label="Hot leads this period" v={cur?.hot_leads ?? 0} last />
        </div>
      )}
    </div>
  );
}

const Cell = ({ label, v, sub, hot, last }) => (
  <div className={`p-5 ${hot ? "bg-acid" : "bg-white"} ${!last ? "border-r-2 border-ink" : ""}`}>
    <div className="overline">{label}</div>
    <div className="font-display font-black text-2xl mt-2 truncate">{v}</div>
    {sub && <div className="font-mono text-[10px] mt-1 text-muted-foreground">{sub}</div>}
  </div>
);

const TradeList = ({ title, rows, money, suffix = "", testid }) => (
  <div className="brut-card bg-white p-6" data-testid={testid}>
    <div className="overline mb-4">// {title}</div>
    {(!rows || rows.length === 0) && <p className="font-mono text-xs text-muted-foreground">Nothing in this window.</p>}
    <div className="space-y-2">
      {(rows || []).map(([name, n]) => (
        <div key={name} className="flex justify-between border-b-2 border-ink pb-2 font-mono text-xs">
          <span className="uppercase truncate pr-3">{name}</span>
          <span className="font-bold">{money ? `$${Number(n).toFixed(2)}` : `${n}${suffix}`}</span>
        </div>
      ))}
    </div>
  </div>
);
